import type { TableContent } from '@unifyapps/defs/blocks/Table/DataTable/types';
import type { UAEntityTableComponentType } from '@unifyapps/defs/blocks/Table/UAEntityTable/types';
import type { EmitActionType } from '@unifyapps/defs/hooks/useBlockAction';
import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import type { Action } from '@unifyapps/defs/types/action';
import type {
  DateRangePresets,
  FilterMenuOptions,
  QuickFilterAction,
} from '@unifyapps/defs/types/blocks';
import type { StyleObject } from '@unifyapps/defs/types/styleObject';
import type { Filter } from '@unifyapps/network/generated/models/filter';
import type { LookupResponse } from '@unifyapps/network/generated/models/lookupResponse';
import type { Sort } from '@unifyapps/network/generated/models/sort';
import type { AutocompleteProps } from '@unifyapps/ui/components/Autocomplete';
import type React from 'react';
import type { ReactNode } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- needed
import type {
  MRT_Cell,
  MRT_Column as MRT_Column_Initial,
  MRT_ColumnDef as MRT_ColumnDef_Initial,
  MRT_FilterOption,
  MRT_Row,
  MRT_RowData,
  MRT_StatefulTableOptions,
  MRT_TableInstance as MRT_TableInstance_Initial,
  MRT_TableOptions as MRT_TableOptions_Initial,
} from './lib/material-react-table/types';
import type { TableStateStore } from './store/tableStateStore';

// oxlint-disable-next-line no-restricted-imports
export * from './lib/material-react-table/types';
export type { MRT_Row };
export type RowSelectionState = Record<string, boolean>;

type AllowedAutocompleteProps = Omit<
  AutocompleteProps<unknown, boolean, boolean, boolean>,
  'onBlur' | 'value'
>;

export type BorderAxisType = 'x' | 'xBetween' | 'y' | 'both' | 'none' | 'yBetween' | 'bothBetween';

export type CustomColumnDef<TData extends MRT_RowData, TValue = unknown> = {
  uiEditSelectProps?:
    | ((props: {
        cell: MRT_Cell<TData, TValue>;
        column: MRT_Column_Initial<TData>;
        row: MRT_Row<TData>;
        table: MRT_TableInstance_Initial<TData>;
      }) => AllowedAutocompleteProps)
    | AllowedAutocompleteProps;
};

export enum Modes {
  Print = 'print',
}

// drives toolbar-scoped styling + internal slot rendering (trigger/refetch/add-button)
// for the filter/sort controls. 'ua-platform' opts into the platform redesign;
// 'default' keeps the base look. Replaces the older filterControlsSlotProps bag.
export const ToolbarVariant = {
  Default: 'default',
  UAPlatform: 'ua-platform',
} as const;
export type ToolbarVariant = (typeof ToolbarVariant)[keyof typeof ToolbarVariant];

// eslint-disable-next-line @typescript-eslint/naming-convention -- exporting using the same name as the original
export type MRT_TableOptions<TData extends MRT_RowData> = MRT_TableOptions_Initial<TData> & {
  renderTopToolbarEndCustomActions?: MRT_TableOptions_Initial<TData>['renderTopToolbarCustomActions'];
  renderColumnVisibilityAction?: () => React.ReactNode;
  renderViewsAction?: () => React.ReactNode;
  renderSaveToViewAction?: (options?: {
    disabled?: boolean;
    filter?: Filter | null;
  }) => React.ReactNode;
  quickFilterActions?: QuickFilterAction[];
  defaultSort?: Sort[] | null;
  enableDateRangeFilter?: boolean;
  enableDateRangePresets?: boolean;
  defaultDateRangePreset?: DateRangePresets;
  enableSort?: boolean;
  enableFilter?: boolean;
  filterProps?: UAEntityTableComponentType['metadata']['filterProps'];
  filterMenuProps?: FilterMenuOptions;
  enableSearchFilter?: boolean;
  alwaysRenderBulkActionsOverlay?: boolean;
  emitAction?: EmitActionType;
  onAction?: (action: Action) => void;
  emitEvent?: EmitEventType;
  refetch?: () => Promise<unknown>;
  toolbarVariant?: ToolbarVariant;
  resizeHandleStyle?: React.CSSProperties;
  isRefetching?: boolean;
  muiTableProps?: MRT_TableOptions_Initial<TData>['muiTableProps'] & {
    borderAxis?: BorderAxisType;
    sx?: Record<string, unknown>;
  };
  useCustomHeaderLayout?: boolean;
  wrapColumnHeader?: boolean;
  // Display only leaf footer cells (actual data columns) instead of maintaining the grouped header structure in footer
  showLeafFooterOnly?: boolean;
  tableStateStore?: TableStateStore;
  renderOverlay?: (params: { table: MRT_TableInstance<TData> }) => React.ReactNode;
  renderTableSheetOverlay?: (params: { table: MRT_TableInstance<TData> }) => React.ReactNode;
  ColumnActionWrapper?: (props: {
    children: React.ReactNode;
    table: MRT_TableInstance<TData>;
    column: MRT_Column<TData>;
    className?: string;
  }) => React.ReactNode;
  mode?: Modes;
  renderCustomToolbar?: () => React.ReactNode;
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- exporting using the same name as the original
export type MRT_TableInstance<TData extends MRT_RowData> = MRT_TableInstance_Initial<TData> & {
  options: MRT_StatefulTableOptions<TData> & {
    renderTopToolbarEndCustomActions?: MRT_StatefulTableOptions<TData>['renderTopToolbarCustomActions'];
    renderColumnVisibilityAction?: () => React.ReactNode;
    renderViewsAction?: () => React.ReactNode;
    renderSaveToViewAction?: (options?: {
      disabled?: boolean;
      filter?: Filter | null;
    }) => React.ReactNode;
    quickFilterActions?: QuickFilterAction[];
    defaultSort?: Sort[] | null;
    enableDateRangeFilter?: boolean;
    enableDateRangePresets?: boolean;
    defaultDateRangePreset?: DateRangePresets;
    enableSort?: boolean;
    enableFilter?: boolean;
    // Controls the layout of filter controls on the top toolbar
    filterVariant?: 'row' | 'column';
    filterProps?: UAEntityTableComponentType['metadata']['filterProps'];
    filterMenuProps?: FilterMenuOptions;
    enableSearchFilter?: boolean;
    alwaysRenderBulkActionsOverlay?: boolean;
    onAction?: (action: Action) => void;
    emitAction?: EmitActionType;
    emitEvent?: EmitEventType;
    refetch?: () => Promise<unknown>;
    toolbarVariant?: ToolbarVariant;
    resizeHandleStyle?: React.CSSProperties;
    isRefetching?: boolean;
    wrapColumnHeader?: boolean;
    tableStateStore?: TableStateStore;
    renderOverlay?: (params: { table: MRT_TableInstance<TData> }) => React.ReactNode;
    renderTableSheetOverlay?: (params: { table: MRT_TableInstance<TData> }) => React.ReactNode;
    useCustomHeaderLayout?: boolean;
    // Display only leaf footer cells (actual data columns) instead of maintaining the grouped header structure in footer
    showLeafFooterOnly?: boolean;
    ColumnActionWrapper?: (props: {
      children: React.ReactNode;
      table: MRT_TableInstance<TData>;
      column: MRT_Column<TData>;
      className?: string;
    }) => React.ReactNode;
    mode?: Modes;
  };
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- exporting using the same name as the original
export type MRT_ColumnDef<TData extends MRT_RowData, TValue = unknown> = MRT_ColumnDef_Initial<
  TData,
  TValue
> & {
  columnComponentProps?: {
    appearance?: Record<string, unknown> & {
      cellStyles?: Pick<StyleObject, 'tableCellPadding'>;
      headerStyles?: Pick<StyleObject, 'tableCellPadding'>;
    };
    addOns?: Record<string, unknown> | null;
    columnAddOns?: Record<string, unknown> | null;
    editCellComponentProps?: Record<string, unknown>;
    booleanType?: 'ICON' | 'TOGGLE';
  };
  footerComponentProps?: {
    appearance?: Record<string, unknown>;
  };
  /**
   * Render the empty state for the column in overlay mode
   */
  renderEmptyState?: ({ column }: { column: MRT_Column<TData, TValue> }) => React.ReactNode;
  /**
   * Aggregation Field Key for the accessorKey of the column
   */
  aggregationFieldKey?: string;
  /**
   * List of actions that can be performed on the column
   */
  columnActions?: TableContent['columns'][number]['columnActions'];
  /**
   * Original Data using which the column was created - to be used in currentColumn context
   */
  originalData?: TableContent['columns'][number]['originalData'];
  /**
   * Type of the column - currently used when type-casting the value
   */
  type?: string;
  /**
   * Computed Column based on the cell context
   */
  getComputedColumnForCell?: (cell: MRT_Cell<TData, TValue>) => Record<string, unknown>;

  /**
   * Wrapper for the cell component, to be used for cases such as custom cell tooltip
   */
  CellWrapper?: (props: {
    children: ReactNode;
    cell: MRT_Cell<TData, TValue>;
    column: MRT_Column<TData>;
    row: MRT_Row<TData>;
    table: MRT_TableInstance<TData>;
    isEditing?: boolean;
    isCreating?: boolean;
  }) => ReactNode;
  /**
   * Start Decorator for ColumnVisibilityMenu
   */
  renderColumnMenuStartDecorator?: (props: { className?: string }) => ReactNode;
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- exporting using the same name as the original
type MRT_DefinedColumnDef<TData extends MRT_RowData, TValue = unknown> = Omit<
  MRT_ColumnDef<TData, TValue>,
  'defaultDisplayColumn' | 'id'
> & {
  _filterFn: MRT_FilterOption;
  defaultDisplayColumn: Partial<MRT_ColumnDef<TData, TValue>>;
  id: string;
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- exporting using the same name as the original
export type MRT_Column<TData extends MRT_RowData, TValue = unknown> = MRT_Column_Initial<TData> & {
  columnDef: MRT_DefinedColumnDef<TData, TValue>;
};

export type RenderCellProps = {
  schemaColumn: UAEntityTableComponentType['columns'][0];
  lookups?: Record<string, LookupResponse | undefined>;
  cell: MRT_Cell<Partial<unknown>>;
  row: MRT_Row<Partial<unknown>>;
  // @ts-expect-error -- fix type
  table: MRT_TableInstance<unknown>;
};
